<template>
	<div class="category-cards w-full">
		<div class="text-center mb-8 sm:mb-10 w-full">
			<h2
				class="text-xl sm:text-2xl md:text-3xl font-bold text-text-primary mb-1.5 sm:mb-2.5"
			>
				What type of insurance do you need?
			</h2>
			<p
				class="text-sm sm:text-base text-text-secondary/90 max-w-2xl mx-auto"
			>
				Choose a category to explore coverage options
			</p>
		</div>

		<div
			class="flex flex-wrap justify-center gap-4 sm:gap-5 md:gap-6 w-full"
		>
			<!-- @click="$emit('category-selected', category.value)" -->
			<div
				v-for="category in categories"
				:key="category.value"
				@click="handleViewProducts"
				class="group relative overflow-hidden bg-surface rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border border-gray-100 hover:border-gray-200 hover:shadow-lg active:shadow-md transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer active:scale-[0.98] w-full sm:w-1/2 lg:w-1/3 xl:w-1/5"
				@touchstart.passive="$el.classList.add('active-scale')"
				@touchend.passive="$el.classList.remove('active-scale')"
			>
				<div
					class="absolute inset-0 bg-gradient-to-br from-primary/6 to-accent/6 opacity-0 group-hover:opacity-100 transition-opacity duration-350"
				></div>

				<div
					class="absolute top-0 right-2 w-20 h-20 sm:w-24 sm:h-24 bg-primary/5 rounded-full blur-xl opacity-50 pointer-events-none"
				></div>

				<div class="relative z-10 p-5 sm:p-6 text-center w-full">
					<div
						class="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 mb-4 rounded-xl bg-gradient-to-br from-primary to-brand-navy shadow-md group-hover:shadow-lg transition-all duration-200"
					>
						<i
							class="text-3xl text-white fas"
							:class="getIconClass(category.value)"
						></i>
					</div>

					<h3
						class="text-lg sm:text-xl font-semibold text-text-primary mb-1.5 sm:mb-2 group-hover:text-primary transition-colors duration-200"
					>
						{{ category.label }}
					</h3>

					<p
						class="text-text-secondary/90 text-xs sm:text-sm leading-snug mb-2.5 sm:mb-3 line-clamp-2"
					>
						{{ getDescription(category.value) }}
					</p>

					<!--					<div class="flex items-center justify-center mb-2 sm:mb-3">-->
					<!--						<div-->
					<!--							class="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap"-->
					<!--						>-->
					<!--							{{ getProductCount(category.value) }} {{ getProductCount(category.value) === 1 ? 'product' : 'products' }}-->
					<!--						</div>-->
					<!--					</div>-->

					<!-- <div class="space-y-1 mb-3 sm:mb-4">
						<div class="h-px bg-gray-100 my-1.5 sm:my-2"></div>
						<div
							v-for="product in getFeaturedProducts(
								category.value,
							)"
							:key="product"
							class="flex items-center justify-center text-sm text-text-secondary"
						>
							<i class="fas fa-check-circle text-accent mr-2"></i>
							{{ product }}
						</div>
					</div> -->

					<button
						@click="handleViewProducts"
						class="inline-flex items-center text-primary text-sm font-medium group-hover:text-primary-dark transition-colors duration-200"
					>
						<span>View Options</span>
						<i
							class="fas fa-chevron-right ml-1.5 text-xs transition-transform duration-200 group-hover:translate-x-0.5"
						></i>
					</button>
				</div>

				<div
					class="absolute inset-0 rounded-xl sm:rounded-2xl border border-transparent group-hover:border-primary/10 transition-colors duration-200 pointer-events-none"
				></div>
			</div>
		</div>
	</div>
</template>

<script>
import products from './products';

export default {
	name: 'CategoryCards',
	props: {
		categories: {
			type: Array,
			required: true,
		},
	},
	methods: {
		getIconClass(value) {
			const icons = {
				individual: 'fa-user-shield',
				business: 'fa-building',
				family: 'fa-users',
				auto: 'fa-car',
				home: 'fa-home',
				health: 'fa-heartbeat',
			};
			return icons[value] || 'fa-shield-alt';
		},
		handleViewProducts() {
			this.$router.push({ name: 'ProductList' });
		},

		getDescription(value) {
			const descriptions = {
				individual:
					'Personal protection solutions including motor insurance, home coverage, health plans, and life assurance tailored for individuals and families.',
				business:
					'Comprehensive commercial insurance solutions covering vehicles, aviation, marine operations, engineering projects, and employee benefits for businesses of all sizes.',
			};

			const fallbackDescriptions = {
				family: 'Family-focused insurance plans that provide peace of mind for every member of your household.',
				auto: 'Complete vehicle protection with coverage options for cars, trucks, motorcycles, and more.',
				home: 'Protect your most valuable asset with comprehensive home and property insurance solutions.',
				health: 'Quality healthcare coverage ensuring you and your family have access to the best medical care.',
			};

			return (
				descriptions[value] ||
				fallbackDescriptions[value] ||
				'Specialized insurance coverage for your unique needs and requirements.'
			);
		},

		getProductCount(value) {
			if (products[value]) {
				return products[value].length;
			}
			return 0;
		},

		getFeaturedProducts(value) {
			if (!products[value]) {
				const fallbackFeatures = {
					family: [
						'Multi-member Discounts',
						'Child Coverage',
						'Senior Options',
					],
					auto: [
						'Collision Coverage',
						'Liability Protection',
						'24/7 Roadside',
					],
					home: [
						'Property Damage',
						'Personal Belongings',
						'Natural Disasters',
					],
					health: [
						'Doctor Visits',
						'Prescription Coverage',
						'Emergency Care',
					],
				};
				return (
					fallbackFeatures[value] || [
						'Comprehensive Coverage',
						'Expert Support',
						'Competitive Rates',
					]
				);
			}

			const categoryProducts = products[value];
			const availableProducts = categoryProducts.filter(
				(product) => product.available,
			);
			const comingSoonProducts = categoryProducts.filter(
				(product) => product.comingSoon,
			);
			const topProducts = [
				...availableProducts,
				...comingSoonProducts,
			].slice(0, 3);
			return topProducts.map((product) => {
				if (product.comingSoon) {
					return `${product.name} (Coming Soon)`;
				}
				return product.name;
			});
		},
	},
};
</script>

<style scoped>
::v-deep .active-scale {
	transform: scale(0.98) !important;
	transition: transform 0.1s ease-out;
}

* {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

@media (pointer: coarse) {
	a,
	button,
	[role='button'] {
		min-height: 44px;
		min-width: 44px;
	}
}

.category-cards {
	@apply px-4 py-12 md:py-16;
}

@media (max-width: 640px) {
	.category-cards {
		@apply px-4;
	}

	@media (max-height: 700px) {
		.category-cards {
			padding-top: 1.5rem;
			padding-bottom: 1.5rem;
		}
	}
}
</style>
